import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { leadsApi } from '@/features/leads/api/leadsApi';
import { LeadNotesForm } from '@/features/leads/components/LeadNotesForm';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import type { LeadDto, UpsertLeadNoteRequest } from '@/shared/types/domain';
import { ApiError } from '@/shared/api/httpClient';

export function LeadDetailPage() {
  const { scanId } = useParams<{ scanId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const leadFromNavigation = (location.state as { lead?: LeadDto } | null)?.lead;
  const [lead, setLead] = useState<LeadDto | undefined>(leadFromNavigation);
  const [isLoading, setIsLoading] = useState(!leadFromNavigation);
  const [error, setError] = useState<string | null>(null);

  // Direct navigation / page reload won't have the lead in router state --
  // fall back to refetching the exhibitor's full lead list and finding it.
  // (A dedicated GET /api/leads/{scanId} endpoint would be a reasonable
  // future optimization if this list grows very large.)
  useEffect(() => {
    if (lead || !user?.exhibitorId || !scanId) return;

    leadsApi
      .getByExhibitor(user.exhibitorId)
      .then((leads) => {
        const found = leads.find((l) => l.scanId === scanId);
        if (!found) {
          setError('Lead not found.');
        } else {
          setLead(found);
        }
      })
      .catch((err) => setError(err instanceof ApiError ? err.userMessage : 'Could not load this lead.'))
      .finally(() => setIsLoading(false));
  }, [lead, scanId, user?.exhibitorId]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-7)' }}>
        <Spinner label="Loading lead" />
      </div>
    );
  }

  if (error || !lead) {
    return <EmptyState icon="⚠" title="Couldn't load this lead" description={error ?? undefined} />;
  }

  async function handleSave(data: UpsertLeadNoteRequest) {
    const updated = await leadsApi.upsertNotes(lead!.scanId, data);
    setLead(updated);
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', marginBottom: 'var(--space-4)', cursor: 'pointer' }}
      >
        ← Back to leads
      </button>

      <div className="stat-card" style={{ marginBottom: 'var(--space-5)' }}>
        <h2 style={{ margin: 0 }}>
          {lead.consentGiven ? `${lead.firstName} ${lead.lastName}` : 'No consent given'}
        </h2>
        {lead.consentGiven ? (
          <>
            <p style={{ margin: '4px 0', color: 'var(--text-muted)' }}>
              {lead.jobTitle} at {lead.company}
            </p>
            <p style={{ margin: '4px 0', color: 'var(--text-muted)' }}>{lead.email}</p>
            {lead.phone && <p style={{ margin: '4px 0', color: 'var(--text-muted)' }}>{lead.phone}</p>}
          </>
        ) : (
          <p style={{ margin: '4px 0', color: 'var(--text-muted)' }}>{lead.consentMessage}</p>
        )}
        <p className="font-mono" style={{ margin: '8px 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
          Badge: {lead.badgeId}
        </p>
      </div>

      <LeadNotesForm lead={lead} onSave={handleSave} />
    </div>
  );
}
